import { useState, useMemo, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import api, { formatErr } from "../lib/api";
import { toast } from "sonner";
import { VerqoLockup } from "../components/VerqoLockup";
import {
  ArrowUpRight, ArrowLeft, ArrowRight, Banknote, ShieldCheck, Zap, Layers,
  CreditCard, Truck, Sparkles, LineChart, Check, CheckCircle2,
  Lock, User as UserIcon, Calendar, Hash, Building2, MapPin, Phone,
  Landmark, TrendingUp, Clock, ChevronDown, ChevronUp, Loader2, AlertCircle,
} from "lucide-react";
import PlaidLinkButton from "../components/PlaidLinkButton";
import LegalFooter from "../components/LegalFooter";
import MasterPolicyApplicationForm from "../components/MasterPolicyApplicationForm";
import VerificationFrame from "../components/VerificationFrame";

const ease = [0.22, 1, 0.36, 1];

// ── Persona-specific marketing copy ──────────────────────────────────────
const COPY = {
  seller: {
    eyebrow: "For Brands & Suppliers",
    headline: "Sell wholesale\nlike it's 2030.",
    sub: "Same-day payouts. Pre-vetted buyers. Zero bad debt. The OS your AR team has been begging for.",
    submitLabel: "Open your seller account",
    formTitle: "Open your seller account",
    formSub: "Four short steps. We onboard your first buyer in 24h.",
    bullets: [
      "Approval in minutes, payouts in 24 hours",
      "Take terms without taking risk — we fund the buyer",
      "Embedded credit lifts AOV by 2.4× on average",
      "Transparent pricing — only pay when you get paid",
    ],
    proofs: [
      { icon: Banknote,    label: "Get paid D+1, every time" },
      { icon: ShieldCheck, label: "We absorb 100% of buyer default risk" },
      { icon: Zap,         label: "3× checkout conversion with Net-60 enabled" },
      { icon: Layers,      label: "One pipe to Shopify, NetSuite, QuickBooks" },
    ],
    quote: "Replaced our factor, killed collections, grew GMV 38% in a quarter.",
    quoteAuthor: "VP Wholesale, Glow Cosmetics",
    metric: { value: "$2.4M", label: "average GMV unlocked in year one" },
  },
  buyer: {
    eyebrow: "For Wholesale Buyers",
    headline: "Stock more.\nPay later.",
    sub: "Instant Net-30/60/90. AI-decisioned in seconds. One login, every supplier — no factoring, no chasing POs.",
    submitLabel: "Get instant credit",
    formTitle: "Claim your line",
    formSub: "Four short steps. Soft pull. No banker, no fax, no nonsense.",
    bullets: [
      "Approved for up to $250K in under 2 minutes",
      "Use it across every supplier on verqo",
      "Transparent fees — no compound interest, ever",
      "Damage, loss & delay insured at checkout",
    ],
    proofs: [
      { icon: CreditCard, label: "Net-30/60/90 — instant decision, soft pull" },
      { icon: Sparkles,   label: "One credit line, every supplier" },
      { icon: Truck,      label: "Insured shipping built into checkout" },
      { icon: LineChart,  label: "Live cash position across every PO" },
    ],
    quote: "Approved for Net-60 in 90 seconds. Doubled our order book without a bank.",
    quoteAuthor: "Founder, Apex Goods",
    metric: { value: "94 sec", label: "median time to credit decision" },
  },
};

const COUNTRIES = [
  { code: "US", name: "United States" },
  { code: "CA", name: "Canada" },
  { code: "GB", name: "United Kingdom" },
  { code: "AU", name: "Australia" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "MX", name: "Mexico" },
  { code: "JP", name: "Japan" },
  { code: "SG", name: "Singapore" },
  { code: "IN", name: "India" },
  { code: "OTHER", name: "Other" },
];

const US_STATES = "AL AK AZ AR CA CO CT DE FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY DC".split(" ");

const BUSINESS_TYPES = [
  { v: "llc",         l: "LLC" },
  { v: "corp",        l: "C-Corp" },
  { v: "s_corp",      l: "S-Corp" },
  { v: "sole_prop",   l: "Sole Proprietor" },
  { v: "partnership", l: "Partnership" },
  { v: "nonprofit",   l: "Nonprofit" },
  { v: "other",       l: "Other" },
];

const BUYER_CATS = [
  "Apparel & Accessories", "Beauty & Personal Care", "Food & Beverage",
  "Home & Kitchen", "Electronics", "Health & Wellness", "Toys & Games",
  "Pet Products", "Sports & Outdoors", "Office & School",
  "Auto & Industrial", "Books & Media",
];

const SELLER_CATS = [
  "Beauty & Personal Care", "Food & Beverage", "Apparel & Accessories",
  "Home & Kitchen", "Health & Wellness", "Baby & Kids", "Pet Supplies",
  "Office & Stationery", "Electronics & Tech", "Outdoor & Sport",
  "Craft & Hobby", "Industrial & B2B Supplies",
];

const GMV_BANDS = [
  { v: "<100k",   l: "Under $100K" },
  { v: "100k-1m", l: "$100K – $1M" },
  { v: "1m-10m",  l: "$1M – $10M" },
  { v: "10m+",    l: "$10M+" },
];

const empty = {
  // Step 1
  first_name: "", last_name: "", email: "", phone: "", password: "",
  // Step 2
  legal_name: "", ein: "", business_type: "", country: "US",
  address_line1: "", address_line2: "", city: "", state: "", postal_code: "",
  // Step 3
  is_legal_owner: true,
  owner_legal_name: "", owner_dob: "", owner_ssn_last4: "",
  owner_phone: "",
  owner_kyc_consent: false,
  // Step 4
  categories: [], soft_check_consent: false, terms_accepted: false,
  // Buyer-only: consent to share derived credit decision with sellers
  credit_share_consent: false,
  // Seller-only step 4
  trade_name: "", year_founded: "", fulfillment_country: "US",
  annual_wholesale_gmv: "",
};

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialRole = params.get("as") === "seller" ? "seller" : "buyer";
  const inviteToken = params.get("invite") || null;
  const prefilledEmail = params.get("email") || "";
  const bypassKey = params.get("key") || null;
  // Post-signup return path (e.g. storefront guest-cart → /s/:slug?checkout=1).
  // Sanitised against open-redirect: same-origin relative paths only.
  const nextRaw = params.get("next") || "";
  const nextPath = (nextRaw.startsWith("/") && !nextRaw.startsWith("//") && !nextRaw.includes("://") && nextRaw.length <= 200) ? nextRaw : null;

  // Jun 2026 — pre-launch waitlist gate. If public signup is disabled
  // AND the URL has no bypass key (internal team) or invite token,
  // redirect to /waitlist so we capture the lead instead of frustrating
  // the user with a blocked signup screen. Fail-OPEN if the status
  // endpoint is unreachable.
  useEffect(() => {
    if (bypassKey || inviteToken) return;  // bypass = internal/approved invite
    let mounted = true;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/signup-status`)
      .then(r => r.json())
      .then(data => {
        if (!mounted) return;
        if (data.public_signup_enabled === false) {
          nav(`/waitlist?as=${initialRole}`, { replace: true });
        }
      })
      .catch(() => { /* fail-open */ });
    return () => { mounted = false; };
  }, [bypassKey, inviteToken, initialRole, nav]);

  const [role, setRole] = useState(initialRole);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ ...empty, email: prefilledEmail });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  // Step 5 (buyers only) — Plaid bank-link uplift
  const [boostScore, setBoostScore] = useState(null); // { institution, accounts_count } | null
  const [verifyOpen, setVerifyOpen] = useState(false);

  // ── EIN verification state (Stage A/B/C gate at step 2) ─────────────
  // phase: idle | precheck_fail | verifying | matched | unmatched | pending | error | not_configured
  const [einVerify, setEinVerify] = useState({ phase: "idle", code: "", message: "", ein: "" });

  // Sync role when query param changes
  useEffect(() => {
    if (params.get("as") && params.get("as") !== role) {
      setRole(params.get("as") === "seller" ? "seller" : "buyer");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const copy = useMemo(() => COPY[role], [role]);

  // ── EIN gate helpers ────────────────────────────────────────────────
  // Any edit to the EIN field invalidates a prior matched/unmatched verdict.
  const setEin = (v) => {
    setForm(f => ({ ...f, ein: v }));
    setEinVerify(s => (s.phase === "idle" ? s : { phase: "idle", code: "", message: "", ein: "" }));
  };
  // Debounced Stage A+B precheck (cheap, instant feedback) on blur / pause typing.
  const runEinPrecheck = async (rawEin) => {
    const ein = (rawEin || "").trim();
    if (!ein) {
      setEinVerify({ phase: "idle", code: "", message: "", ein: "" });
      return;
    }
    try {
      const { data } = await api.post("/auth/ein/precheck", { ein });
      if (data?.ok) {
        // Don't surface a "success" pill on precheck — Stage C (Middesk) is the real gate.
        setEinVerify({ phase: "idle", code: "ok", message: "", ein: data.ein || ein });
      } else {
        setEinVerify({
          phase: "precheck_fail",
          code: data?.code || "format_invalid",
          message: data?.message || "EIN looks invalid.",
          ein: data?.ein || ein,
        });
      }
    } catch {
      // Network/server hiccup on precheck is non-blocking — Stage C will catch it.
      setEinVerify({ phase: "idle", code: "", message: "", ein: "" });
    }
  };
  // Stage C — full Middesk verify, called from next() at step 2. Hard-blocks.
  const runEinVerify = async () => {
    setEinVerify(s => ({ ...s, phase: "verifying", message: "" }));
    try {
      const { data } = await api.post("/auth/ein/verify", {
        ein: form.ein.trim(),
        legal_name: form.legal_name.trim(),
        address: {
          address_line1: form.address_line1.trim(),
          address_line2: form.address_line2.trim() || undefined,
          city: form.city.trim(),
          state: form.state.trim(),
          postal_code: form.postal_code.trim(),
          country: form.country || "US",
        },
      });
      const next_phase = data?.ok
        ? "matched"
        : (data?.code === "format_invalid" || data?.code === "prefix_invalid" || data?.code === "fake_pattern")
          ? "precheck_fail"
          : (data?.code === "unmatched" ? "unmatched"
              : data?.code === "pending" ? "pending"
              : data?.code === "not_configured" ? "not_configured"
              : "error");
      setEinVerify({
        phase: next_phase,
        code: data?.code || "error",
        message: data?.ok ? "" : (data?.message || "Hmm, that doesn't match an IRS record — double-check?"),
        ein: data?.ein || form.ein.trim(),
      });
      return data?.ok === true;
    } catch (e) {
      setEinVerify({
        phase: "error",
        code: "error",
        message: "Verification service hiccuped — please retry.",
        ein: form.ein.trim(),
      });
      return false;
    }
  };

  const toggleCategory = (c) => setForm(f => {
    const has = f.categories.includes(c);
    if (has) return { ...f, categories: f.categories.filter(x => x !== c) };
    if (f.categories.length >= 3) return f;
    return { ...f, categories: [...f.categories, c] };
  });

  // ── Validators ─────────────────────────────────────────────────────
  const einValid = /^\d{2}-?\d{7}$/.test((form.ein || "").trim());
  const phoneDigits = (s) => (s || "").replace(/\D/g, "");

  // Step 2 cannot be "valid" (i.e. Continue cannot land us on step 3) until
  // Stage C (Middesk) returns matched for THIS exact EIN. The button still
  // visually enables on local format pass so click-to-verify is intuitive.
  const einLocallyOk = einValid && einVerify.phase !== "precheck_fail";
  const einMatchedForCurrentValue = (
    einVerify.phase === "matched" && einVerify.ein === (form.ein || "").trim().replace(/^(\d{2})(\d{7})$/, "$1-$2")
  );

  const validStep = useMemo(() => {
    if (step === 1) {
      return form.first_name.trim().length >= 1
        && form.last_name.trim().length >= 1
        && /^\S+@\S+\.\S+$/.test(form.email.trim())
        && phoneDigits(form.phone).length >= 7
        && form.password.length >= 8;
    }
    if (step === 2) {
      return form.legal_name.trim().length >= 2
        && einLocallyOk
        && !!form.business_type
        && !!form.country
        && form.address_line1.trim().length >= 1
        && form.city.trim().length >= 1
        && form.state.trim().length >= 1
        && form.postal_code.trim().length >= 1;
    }
    if (step === 3) {
      const okBase = form.owner_legal_name.trim().length >= 2
        && /^\d{4}-\d{2}-\d{2}$/.test(form.owner_dob)
        && /^\d{4}$/.test(form.owner_ssn_last4)
        && form.owner_kyc_consent;
      // 18+ check
      if (!okBase) return false;
      const dob = new Date(form.owner_dob);
      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const m = today.getMonth() - dob.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age -= 1;
      if (age < 18 || age > 120) return false;
      // owner phone required if signup user is NOT the legal owner
      if (!form.is_legal_owner && phoneDigits(form.owner_phone).length < 7) return false;
      return true;
    }
    if (step === 4) {
      const baseOk = form.categories.length >= 1
        && form.categories.length <= 3
        && form.soft_check_consent
        && form.terms_accepted;
      if (!baseOk) return false;
      if (role === "buyer" && !form.credit_share_consent) return false;
      if (role === "seller") {
        return !!form.annual_wholesale_gmv && !!form.fulfillment_country;
      }
      return true;
    }
    return false;
  }, [form, step, role, einLocallyOk]);

  const next = async (e) => {
    if (e) e.preventDefault();
    setErr("");
    if (!validStep) return;
    // Step 2 gate — hard-block until Middesk confirms an IRS TIN match for
    // the current EIN value. If we already matched this exact EIN, advance.
    if (step === 2 && !einMatchedForCurrentValue) {
      const ok = await runEinVerify();
      if (!ok) return; // stay on step 2; einVerify state surfaces the error inline
    }
    if (step < 4) setStep(s => s + 1);
    else void submit();
  };

  const submit = async () => {
    setLoading(true);
    setErr("");
    try {
      const payload = {
        // Step 1
        name: `${form.first_name.trim()} ${form.last_name.trim()}`,
        first_name: form.first_name.trim(),
        last_name:  form.last_name.trim(),
        email:      form.email.trim(),
        phone:      form.phone.trim(),
        password:   form.password,
        role,
        // Step 2
        company:        form.legal_name.trim(),
        ein:            form.ein.trim(),
        business_type:  form.business_type,
        country:        form.country,
        address_line1:  form.address_line1.trim(),
        address_line2:  form.address_line2.trim() || undefined,
        city:           form.city.trim(),
        state:          form.state.trim(),
        postal_code:    form.postal_code.trim(),
        // Step 3
        owner_legal_name:  form.owner_legal_name.trim(),
        owner_dob:         form.owner_dob,
        owner_ssn_last4:   form.owner_ssn_last4,
        owner_kyc_consent: !!form.owner_kyc_consent,
        is_legal_owner:    !!form.is_legal_owner,
        owner_phone:       form.is_legal_owner ? undefined : form.owner_phone.trim(),
        // Step 4
        categories:        form.categories,
        soft_check_consent: !!form.soft_check_consent,
        terms_accepted:     !!form.terms_accepted,
        ...(role === "buyer" ? { credit_share_consent: !!form.credit_share_consent } : {}),
        // Seller-only step 4
        ...(role === "seller" ? {
          trade_name:           form.trade_name.trim() || undefined,
          fulfillment_country:  form.fulfillment_country || undefined,
          year_founded:         form.year_founded ? Number(form.year_founded) : undefined,
          annual_wholesale_gmv: form.annual_wholesale_gmv || undefined,
          brand_category:       form.categories[0] || undefined,
        } : {}),
      };
      const u = await register(payload, { bypassKey });
      toast.success(`Account created. Welcome, ${u.name}`);
      if (inviteToken && u.role === "buyer") {
        try {
          const api = (await import("../lib/api")).default;
          const { data } = await api.post(`/invites/accept/${inviteToken}`);
          toast.success(`Connected to ${data.tenant_name}`);
          localStorage.setItem("verqo_active_supplier_slug", data.tenant_slug);
          nav("/buyer/catalog");
          return;
        } catch { /* fallthrough */ }
      }
      // Buyers (no invite) → optional Plaid bank-link uplift step.
      // Sellers → Allianz Trade master-policy opt-in step.
      // EXCEPT when a `next` return path is present (e.g. a storefront
      // guest-cart waiting to check out) — skip the uplift and return there.
      if (nextPath) {
        nav(nextPath);
        return;
      }
      if (u.role === "buyer" && !inviteToken) {
        setStep(5);
        return;
      }
      if (u.role === "seller") {
        setStep(5);
        return;
      }
      nav(`/${u.role}`);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const switchRole = (r) => { setRole(r); setStep(1); };

  // Business verification runs at the end of signup, inside Verqo. Whatever the
  // outcome, the member lands in their portal — a pending check never blocks
  // them from looking around.
  const finishVerification = (openState) => {
    setVerifyOpen(openState);
    if (!openState) nav(`/${role}`);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* LEFT — wizard */}
      <div className="p-8 lg:p-14 flex flex-col">
        <Link to="/" className="flex items-center mb-10 lg:mb-14" data-testid="register-back">
          <VerqoLockup size="md" />
        </Link>

        <div className="flex-1 flex items-start lg:items-center">
          <div className="w-full max-w-md">
            <div className="inline-flex p-1 rounded-full border border-foreground/15 bg-white/70 backdrop-blur text-[12px] mono uppercase tracking-[0.18em]"
              data-testid="register-persona-toggle">
              <button type="button" onClick={() => switchRole("buyer")} data-testid="role-buyer"
                className={`px-3.5 py-1.5 rounded-full transition-all ${role === "buyer" ? "bg-foreground text-background" : "text-foreground/60 hover:text-foreground"}`}>
                I'm a Buyer
              </button>
              <button type="button" onClick={() => switchRole("seller")} data-testid="role-seller"
                className={`px-3.5 py-1.5 rounded-full transition-all ${role === "seller" ? "bg-foreground text-background" : "text-foreground/60 hover:text-foreground"}`}>
                I'm a Seller
              </button>
            </div>

            <AnimatePresence mode="wait">
              <motion.div key={role}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.35, ease }}>
                <h1 className="display text-4xl sm:text-5xl mt-6 leading-[1.05] tracking-tight">{copy.formTitle}</h1>
                <p className="text-foreground/60 mt-3 text-[15px]">{copy.formSub}</p>
                <ul className="mt-6 space-y-2">
                  {copy.bullets.map(b => (
                    <li key={b} className="flex items-start gap-2.5 text-[14px] text-foreground/80">
                      <Check className="w-4 h-4 mt-0.5 text-foreground shrink-0"/>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </AnimatePresence>

            <Stepper step={step} role={role} data-testid={`reg-step-${step}`}/>

            {step === 5 ? (
              role === "seller" ? (
                <Step5SellerInsurance
                  onDone={() => setVerifyOpen(true)}
                />
              ) : (
                <Step5BoostScore
                  role={role}
                  boostScore={boostScore}
                  onConnected={(data) => {
                    setBoostScore({
                      institution: data?.institution?.name || "your bank",
                      accounts: data?.ach_capable ?? 0,
                    });
                    toast.success("Score updated — bank connected");
                  }}
                  onDone={() => setVerifyOpen(true)}
                />
              )
            ) : (
              <>
              <form onSubmit={next} className="mt-7 space-y-4" data-testid={`reg-step-${step}-form`}>
              {step === 1 && <Step1 form={form} set={set}/>}
              {step === 2 && <Step2 form={form} set={set} setEin={setEin} einVerify={einVerify} runEinPrecheck={runEinPrecheck}/>}
              {step === 3 && <Step3 form={form} set={set}/>}
              {step === 4 && <Step4 role={role} form={form} set={set} toggleCategory={toggleCategory}/>}

              {err && <div className="text-sm text-destructive" data-testid="reg-error">{err}</div>}

              <div className="flex items-center gap-3 pt-2">
                {step > 1 && (
                  <Button type="button" variant="ghost" onClick={() => setStep(s => s - 1)}
                    data-testid="reg-back-btn"
                    className="rounded-full h-11 px-5">
                    <ArrowLeft className="w-4 h-4 mr-1.5"/> Back
                  </Button>
                )}
                <Button
                  type="submit"
                  disabled={!validStep || loading || einVerify.phase === "verifying"}
                  data-testid={`reg-${step === 4 ? "submit" : "continue"}-btn`}
                  className="flex-1 h-11 rounded-full bg-foreground text-background hover:bg-foreground/90 text-[14px] font-medium disabled:opacity-50">
                  {einVerify.phase === "verifying" && step === 2
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/> Verifying with IRS…</>
                    : loading
                      ? "Creating…"
                      : step < 4
                        ? <>Continue <ArrowUpRight className="w-4 h-4 ml-1"/></>
                        : <>{copy.submitLabel} <ArrowUpRight className="w-4 h-4 ml-1"/></>}
                </Button>
              </div>
              {step === 4 && (
                <p className="text-[11.5px] text-foreground/50 leading-relaxed">
                  By creating your account you agree to our Terms &amp; Privacy.
                  Soft pull only — no impact on personal credit.
                  {role === "buyer" && (
                    <>
                      {" "}If you receive credit terms, unpaid invoices may be referred to{" "}
                      <a href="/legal/insurance" target="_blank" rel="noreferrer" className="underline" data-testid="reg-tci-link">Allianz Trade for collections</a>.
                    </>
                  )}
                </p>
              )}
            </form>

            {/* Google sign-up · only shown on Step 1 (visitor hasn't typed an
                email yet so we let Google supply their identity instead). */}
            {step === 1 && (
              <>
                <div className="flex items-center gap-3 mt-6 text-[11.5px] text-foreground/45 mono uppercase tracking-[0.18em]">
                  <div className="flex-1 h-px bg-foreground/10"/>or<div className="flex-1 h-px bg-foreground/10"/>
                </div>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const { data } = await api.get(`/auth/google/authorize?role=${role}`);
                      if (data?.authorize_url) {
                        window.location.href = data.authorize_url;
                      } else {
                        toast.error("Google sign-up is unavailable right now. Try again in a moment.");
                      }
                    } catch (e) {
                      const detail = e?.response?.data?.detail;
                      const msg = detail?.message || (typeof detail === "string" ? detail : null) || "Google sign-up failed to start. Please try again.";
                      toast.error(msg);
                    }
                  }}
                  data-testid="register-google"
                  className="mt-4 w-full h-11 rounded-full border border-border bg-background hover:bg-secondary/60 transition-colors flex items-center justify-center gap-2.5 text-[14px] font-medium"
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
                    <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
                    <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
                    <path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 0 1 3.68 9c0-.593.102-1.17.284-1.706V4.962H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"/>
                    <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z"/>
                  </svg>
                  Sign up with Google as {role === "seller" ? "Seller" : "Buyer"}
                </button>
                <p className="mt-2 text-center text-[11px] text-foreground/45">
                  Faster than typing — we'll prefill name + email from your Google profile.
                </p>
              </>
            )}
            </>
            )}

            <div className="mt-6 text-sm text-foreground/70">
              Already have an account?{" "}
              <Link to={`/login?as=${role}`}
                className="underline underline-offset-4 decoration-foreground/30 hover:decoration-foreground"
                data-testid="reg-to-login">
                Sign in
              </Link>
            </div>
          </div>
        </div>

        <LegalFooter />
      </div>

      {/* RIGHT — persona panel */}
      <div className="hidden lg:block relative bg-foreground text-background overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-[0.07]"/>
        <div className="absolute -right-24 -bottom-24 w-[420px] h-[420px] rounded-full bg-accent/30 blur-3xl"/>
        <div className="absolute -left-20 top-1/4 w-72 h-72 rounded-full bg-accent/10 blur-3xl"/>
        <div className="relative p-14 xl:p-16 h-full flex flex-col justify-between">
          <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-accent/90 inline-flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-accent shadow-[0_0_10px_currentColor]"/>
            {copy.eyebrow}
          </div>
          <AnimatePresence mode="wait">
            <motion.div key={role}
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.45, ease }}>
              <div className="display text-5xl xl:text-6xl leading-[1.02] tracking-tight whitespace-pre-line">{copy.headline}</div>
              <p className="text-background/65 mt-6 max-w-md text-[15px] leading-relaxed">{copy.sub}</p>
              <div className="mt-10 inline-flex items-baseline gap-3 border-l-2 border-accent pl-4">
                <span className="display text-4xl text-accent">{copy.metric.value}</span>
                <span className="text-[12px] mono uppercase tracking-[0.18em] text-background/60 max-w-[180px]">{copy.metric.label}</span>
              </div>
              <ul className="mt-10 space-y-3.5 max-w-md">
                {copy.proofs.map((p, i) => {
                  const Icon = p.icon;
                  return (
                    <motion.li key={p.label}
                      initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 + i * 0.06, duration: 0.5, ease }}
                      className="flex items-start gap-3">
                      <span className="mt-0.5 w-7 h-7 rounded-md bg-accent/15 ring-1 ring-accent/30 grid place-items-center shrink-0">
                        <Icon className="w-3.5 h-3.5 text-accent"/>
                      </span>
                      <span className="text-[14.5px] text-background/85">{p.label}</span>
                    </motion.li>
                  );
                })}
              </ul>
            </motion.div>
          </AnimatePresence>
          <div className="border-t border-background/15 pt-6 max-w-md">
            <p className="text-[15px] text-background/85 italic leading-relaxed">"{copy.quote}"</p>
            <p className="mt-2 text-[12px] mono uppercase tracking-[0.18em] text-background/55">{copy.quoteAuthor}</p>
          </div>
        </div>
      </div>

      <VerificationFrame
        open={verifyOpen}
        onOpenChange={finishVerification}
        onApproved={() => nav(`/${role}`)}
      />
    </div>
  );
}

// ─── Steps ──────────────────────────────────────────────────────────────────

function Step1({ form, set }) {
  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <Field label="First name">
          <Input value={form.first_name} onChange={e => set("first_name", e.target.value)}
            autoComplete="given-name" required data-testid="reg-first-name"
            className="h-11 bg-white"/>
        </Field>
        <Field label="Last name">
          <Input value={form.last_name} onChange={e => set("last_name", e.target.value)}
            autoComplete="family-name" required data-testid="reg-last-name"
            className="h-11 bg-white"/>
        </Field>
      </div>
      <Field label="Work email">
        <Input type="email" value={form.email} onChange={e => set("email", e.target.value)}
          autoComplete="email" required placeholder="you@company.com"
          data-testid="reg-email"
          className="h-11 bg-white"/>
      </Field>
      <Field label="Mobile phone" icon={Phone} hint="Used for account verification, fraud alerts, and admin contact">
        <Input type="tel" value={form.phone} onChange={e => set("phone", e.target.value)}
          autoComplete="tel" required placeholder="+1 (415) 555-2671"
          data-testid="reg-phone"
          className="h-11 bg-white"/>
      </Field>
      <Field label="Password" hint="At least 8 characters">
        <Input type="password" value={form.password} onChange={e => set("password", e.target.value)}
          autoComplete="new-password" required minLength={8}
          data-testid="reg-password"
          className="h-11 bg-white"/>
      </Field>
    </>
  );
}

function Step2({ form, set, setEin, einVerify, runEinPrecheck }) {
  const phase = einVerify?.phase || "idle";
  const hasInlineError = phase === "precheck_fail" || phase === "unmatched" || phase === "pending" || phase === "error" || phase === "not_configured";
  const ringClass =
    phase === "matched" ? "ring-1 ring-emerald-500/40"
    : hasInlineError ? "ring-1 ring-rose-500/50"
    : "";
  return (
    <>
      <Field label="Legal business name *" icon={Building2}>
        <Input value={form.legal_name} onChange={e => set("legal_name", e.target.value)}
          placeholder="ACME Wholesale LLC"
          data-testid="reg-legal-name"
          className="h-11 bg-white"/>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="EIN (Federal Tax ID) *" hint="Format 12-3456789">
          <Input
            value={form.ein}
            onChange={e => setEin(e.target.value)}
            onBlur={e => { void runEinPrecheck(e.target.value); }}
            maxLength={10} placeholder="12-3456789"
            data-testid="reg-ein"
            className={`h-11 bg-white font-mono ${ringClass}`}
            aria-invalid={hasInlineError}
          />
        </Field>
        <Field label="Business type *">
          <select value={form.business_type} onChange={e => set("business_type", e.target.value)}
            data-testid="reg-business-type"
            className="h-11 w-full rounded-md border border-border bg-white px-3 text-sm">
            <option value="">Pick one…</option>
            {BUSINESS_TYPES.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
          </select>
        </Field>
      </div>
      {/* EIN status pill — inline, friendly, allows immediate re-edit */}
      {phase === "matched" && (
        <div className="-mt-1 flex items-center gap-2 text-[12px] text-emerald-700" data-testid="reg-ein-status-matched">
          <CheckCircle2 className="w-3.5 h-3.5"/>
          <span>EIN verified with IRS records.</span>
        </div>
      )}
      {hasInlineError && (
        <div
          className="-mt-1 flex items-start gap-2 text-[12px] text-rose-700 bg-rose-50/70 border border-rose-200/70 rounded-md px-3 py-2"
          data-testid={`reg-ein-status-${phase}`}
        >
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0"/>
          <span>{einVerify.message || "Hmm, that doesn't match an IRS record — double-check?"}</span>
        </div>
      )}
      <Field label="Country *">
        <select value={form.country} onChange={e => set("country", e.target.value)}
          data-testid="reg-country"
          className="h-11 w-full rounded-md border border-border bg-white px-3 text-sm">
          {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
        </select>
      </Field>

      <div className="rounded-xl bg-secondary/40 border border-border p-4 space-y-3">
        <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55 inline-flex items-center gap-1.5">
          <MapPin className="w-3 h-3"/> Business address
        </div>
        <Field label="Street *">
          <Input value={form.address_line1} onChange={e => set("address_line1", e.target.value)}
            placeholder="123 Main St" data-testid="reg-addr1"
            className="h-10 bg-white"/>
        </Field>
        <Field label="Suite / Unit (optional)">
          <Input value={form.address_line2} onChange={e => set("address_line2", e.target.value)}
            data-testid="reg-addr2"
            className="h-10 bg-white"/>
        </Field>
        <div className="grid grid-cols-3 gap-2">
          <Field label="City *">
            <Input value={form.city} onChange={e => set("city", e.target.value)}
              data-testid="reg-city"
              className="h-10 bg-white"/>
          </Field>
          <Field label="State *">
            <select value={form.state} onChange={e => set("state", e.target.value)}
              data-testid="reg-state"
              className="h-10 w-full rounded-md border border-border bg-white px-3 text-sm">
              <option value="">…</option>
              {US_STATES.map(s => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="ZIP *">
            <Input value={form.postal_code} onChange={e => set("postal_code", e.target.value)}
              maxLength={10} data-testid="reg-zip"
              className="h-10 bg-white"/>
          </Field>
        </div>
      </div>
    </>
  );
}

function Step3({ form, set }) {
  return (
    <>
      <div className="rounded-xl bg-secondary/40 border border-border p-3.5 flex items-start gap-3">
        <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-foreground/70"/>
        <div className="text-[12.5px] text-foreground/70 leading-relaxed">
          One-time identity check required by our banking partner.
          <strong className="text-foreground"> We never store the full SSN</strong> — only the last 4 digits, encrypted at rest.
        </div>
      </div>

      <label className="flex items-start gap-2.5 cursor-pointer rounded-xl border border-border bg-white p-3.5 hover:border-foreground/30 transition-colors"
        data-testid="reg-is-owner">
        <Checkbox checked={form.is_legal_owner} onCheckedChange={v => set("is_legal_owner", !!v)}/>
        <div>
          <div className="text-[13px] font-medium text-foreground">I am the legal owner of this business</div>
          <div className="text-[11.5px] text-foreground/55 leading-relaxed mt-0.5">
            Uncheck if you're signing up on behalf of someone else (e.g. CFO, assistant). We'll then ask for the owner's phone.
          </div>
        </div>
      </label>

      <Field label="Legal owner full name *" icon={UserIcon} hint="As it appears on government ID">
        <Input value={form.owner_legal_name} onChange={e => set("owner_legal_name", e.target.value)}
          placeholder="Jane A. Smith" data-testid="reg-owner-name"
          className="h-11 bg-white"/>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Date of birth *" icon={Calendar} hint="Must be 18+">
          <Input type="date" value={form.owner_dob} onChange={e => set("owner_dob", e.target.value)}
            max={new Date().toISOString().slice(0, 10)}
            data-testid="reg-owner-dob"
            className="h-11 bg-white font-mono"/>
        </Field>
        <Field label="Last 4 of SSN *" icon={Hash} hint="Last 4 digits only">
          <Input value={form.owner_ssn_last4}
            onChange={e => set("owner_ssn_last4", e.target.value.replace(/\D/g, "").slice(0, 4))}
            placeholder="••••" inputMode="numeric" maxLength={4}
            data-testid="reg-owner-ssn4"
            className="h-11 bg-white font-mono tracking-[0.4em]"/>
        </Field>
      </div>

      {!form.is_legal_owner && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3"
          data-testid="reg-owner-phone-block">
          <div className="text-[11px] mono uppercase tracking-[0.18em] text-amber-800 inline-flex items-center gap-1.5">
            <Phone className="w-3 h-3"/> Owner contact required
          </div>
          <Field label="Legal owner's mobile phone *" icon={Phone}
            hint="We'll reach the owner directly for identity verification.">
            <Input type="tel" value={form.owner_phone} onChange={e => set("owner_phone", e.target.value)}
              placeholder="+1 (415) 555-2671" autoComplete="tel"
              data-testid="reg-owner-phone"
              className="h-11 bg-white"/>
          </Field>
        </div>
      )}

      <label className="flex items-start gap-2.5 cursor-pointer pt-1" data-testid="reg-owner-consent">
        <Checkbox checked={form.owner_kyc_consent} onCheckedChange={v => set("owner_kyc_consent", !!v)}/>
        <span className="text-[12.5px] leading-relaxed text-foreground/75">
          {form.is_legal_owner
            ? "I am the beneficial owner and authorize verqo and its banking partners to verify my identity using the information above."
            : "I am authorized to act on behalf of the legal owner and authorize verqo and its banking partners to verify their identity using the information above."}
        </span>
      </label>
      <p className="text-[11px] text-foreground/50 leading-relaxed inline-flex items-start gap-1.5">
        <Lock className="w-3 h-3 mt-0.5 shrink-0"/>
        Bank-grade encryption · last-4 only · soft check, no hard credit pull.
      </p>
    </>
  );
}

function Step4({ role, form, set, toggleCategory }) {
  const cats = role === "buyer" ? BUYER_CATS : SELLER_CATS;
  const subtitle = role === "buyer"
    ? "Pick up to 3 categories that best describe what you stock. Helps us match you with the right suppliers."
    : "Pick up to 3 brand categories. The first one becomes your primary category and drives storefront classification.";
  const [showAllPolicies, setShowAllPolicies] = useState(false);
  const consentCount = role === "buyer" ? 3 : 2;
  return (
    <>
      <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/45">
        {role === "buyer" ? "What do you buy?" : "Tell buyers about your brand"}
      </div>
      <p className="text-[13px] text-foreground/60 leading-relaxed">{subtitle}</p>

      {/* Seller-only: trade name + year founded */}
      {role === "seller" && (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Trade name (DBA)" hint="Defaults to legal name if blank">
            <Input value={form.trade_name} onChange={e => set("trade_name", e.target.value)}
              placeholder="Glow Cosmetics" data-testid="reg-trade-name"
              className="h-11 bg-white"/>
          </Field>
          <Field label="Year founded">
            <Input type="number" min={1800} max={new Date().getFullYear()}
              value={form.year_founded} onChange={e => set("year_founded", e.target.value)}
              placeholder="2018" data-testid="reg-year-founded"
              className="h-11 bg-white font-mono"/>
          </Field>
        </div>
      )}

      <Field label={role === "seller" ? "Brand categories *" : "Categories *"}
        hint={`${form.categories.length}/3 selected`}>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {cats.map(c => {
            const sel = form.categories.includes(c);
            return (
              <button key={c} type="button" onClick={() => toggleCategory(c)}
                data-testid={`reg-cat-${c.replace(/[^a-z]+/gi, '-').toLowerCase()}`}
                className={`text-left text-[12.5px] rounded-lg border px-3 py-2.5 transition-all ${
                  sel
                    ? "border-foreground bg-foreground text-background"
                    : "border-border bg-white hover:border-foreground/40"
                }`}>
                <div className="flex items-center gap-2">
                  {sel && <CheckCircle2 className="w-3.5 h-3.5"/>} {c}
                </div>
              </button>
            );
          })}
        </div>
      </Field>

      {/* Seller-only: GMV + fulfillment country */}
      {role === "seller" && (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Annual wholesale GMV *" hint="Right-sizes your account tier">
            <select value={form.annual_wholesale_gmv} onChange={e => set("annual_wholesale_gmv", e.target.value)}
              data-testid="reg-gmv"
              className="h-11 w-full rounded-md border border-border bg-white px-3 text-sm">
              <option value="">Pick a range…</option>
              {GMV_BANDS.map(g => <option key={g.v} value={g.v}>{g.l}</option>)}
            </select>
          </Field>
          <Field label="Fulfillment country *" hint="Where your warehouse ships from">
            <select value={form.fulfillment_country} onChange={e => set("fulfillment_country", e.target.value)}
              data-testid="reg-fulfillment-country"
              className="h-11 w-full rounded-md border border-border bg-white px-3 text-sm">
              {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
          </Field>
        </div>
      )}

      <div className="rounded-xl bg-secondary/40 border border-border p-4 mt-2">
        <div className="flex items-center justify-between mb-1">
          <div className="display text-base flex items-center gap-2">
            <ShieldCheck className="w-4 h-4"/> Authorizations
          </div>
          <span className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/45" data-testid="reg-consent-count">
            {consentCount} quick consents
          </span>
        </div>
        <p className="text-[11.5px] text-foreground/55 leading-relaxed mb-3">
          Each consent below is separate so you know exactly what you're authorizing.
        </p>

        <div className="space-y-1.5">
          {/* 1 — Soft credit check (FCRA requires this to be a separate authorization) */}
          <label className="flex items-start gap-2.5 cursor-pointer p-2.5 rounded-md hover:bg-white/40 transition-colors" data-testid="reg-soft-check">
            <Checkbox checked={form.soft_check_consent} onCheckedChange={v => set("soft_check_consent", !!v)} className="mt-0.5"/>
            <span className="text-[12.5px] leading-relaxed">
              <strong className="font-medium">Soft business credit check.</strong>{" "}
              <span className="text-foreground/65">{role === "buyer"
                ? "No impact on your personal credit score."
                : "Used to underwrite seller risk — soft pull only, no impact on personal credit."}</span>
            </span>
          </label>

          {/* 2 — Buyer-only: credit-share opt-in (FCRA-style data sharing must be granular) */}
          {role === "buyer" && (
            <label className="flex items-start gap-2.5 cursor-pointer p-2.5 rounded-md hover:bg-white/40 transition-colors" data-testid="reg-credit-share">
              <Checkbox
                checked={form.credit_share_consent}
                onCheckedChange={v => set("credit_share_consent", !!v)}
                className="mt-0.5"
                data-testid="reg-credit-share-checkbox"
              />
              <span className="text-[12.5px] leading-relaxed">
                <strong className="font-medium">Share my Verqo Trade Score with connected sellers.</strong>{" "}
                <span className="text-foreground/65">Descriptive on-platform behavior only — never raw bank data. Informational, not a credit decision. You can opt out or dispute anytime.</span>{" "}
                <a href="/legal/trade-score" target="_blank" rel="noreferrer" className="underline hover:text-foreground">Methodology</a>.
              </span>
            </label>
          )}

          {/* 3 — Master Terms (E-SIGN affirmative click-through) +
                  change-of-terms clause (Stripe/Shopify pattern). The clause
                  is the legal anchor that lets us update policies without
                  blocking active users — it's part of the same checkbox text
                  so the audit trail captures it via checkbox_text_snapshot. */}
          <label className="flex items-start gap-2.5 cursor-pointer p-2.5 rounded-md hover:bg-white/40 transition-colors" data-testid="reg-terms">
            <Checkbox checked={form.terms_accepted} onCheckedChange={v => set("terms_accepted", !!v)} className="mt-0.5"/>
            <span className="text-[12.5px] leading-relaxed">
              I agree to verqo's{" "}
              <a href="/legal/terms"   target="_blank" rel="noreferrer" className="underline hover:text-foreground">Terms</a>,{" "}
              <a href="/legal/privacy" target="_blank" rel="noreferrer" className="underline hover:text-foreground">Privacy</a>, and{" "}
              <a href="/legal/esign"   target="_blank" rel="noreferrer" className="underline hover:text-foreground">E-Sign Consent</a>.{" "}
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); setShowAllPolicies(s => !s); }}
                className="text-foreground/60 hover:text-foreground inline-flex items-center gap-0.5"
                data-testid="reg-toggle-all-policies"
              >
                <span className="underline underline-offset-2">{showAllPolicies ? "Hide all policies" : "See all policies"}</span>
                {showAllPolicies ? <ChevronUp className="w-3 h-3"/> : <ChevronDown className="w-3 h-3"/>}
              </button>
              <span
                className="block mt-1.5 text-[11.5px] text-foreground/60 leading-relaxed"
                data-testid="reg-change-of-terms-clause"
              >
                I understand verqo may update these policies from time to time. We&apos;ll email you when material changes take effect, and my continued use of verqo after the effective date confirms my acceptance of the updated terms.
              </span>
              {role === "seller" && <span className="block mt-1 text-foreground/60">I confirm I&apos;m authorized to publish this brand on verqo.</span>}
            </span>
          </label>
        </div>

        {/* Expandable full policy list — replaces the old separate "Your rights & notices" strip */}
        {showAllPolicies && (
          <div
            className="mt-3 pt-3 border-t border-foreground/10 grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1.5 text-[11.5px]"
            data-testid="reg-all-policies"
          >
            <a href="/legal/terms"             target="_blank" rel="noreferrer" className="underline hover:text-foreground text-foreground/70">Terms of Service</a>
            <a href="/legal/privacy"           target="_blank" rel="noreferrer" className="underline hover:text-foreground text-foreground/70">Privacy Policy</a>
            <a href="/legal/esign"             target="_blank" rel="noreferrer" className="underline hover:text-foreground text-foreground/70">E-Sign Consent</a>
            <a href="/legal/acceptable-use"    target="_blank" rel="noreferrer" className="underline hover:text-foreground text-foreground/70">Acceptable Use</a>
            <a href="/legal/cookies"           target="_blank" rel="noreferrer" className="underline hover:text-foreground text-foreground/70">Cookie Policy</a>
            <a href="/legal/aml-kyc"           target="_blank" rel="noreferrer" className="underline hover:text-foreground text-foreground/70">AML &amp; KYC</a>
            <a href="/legal/aml-kyc"           target="_blank" rel="noreferrer" className="underline hover:text-foreground text-foreground/70">Payment Holding</a>
            <a href="/legal/trade-score"       target="_blank" rel="noreferrer" className="underline hover:text-foreground text-foreground/70">Trade Score Methodology</a>
            <a href="/legal/do-not-sell"       target="_blank" rel="noreferrer" className="underline hover:text-foreground text-foreground/70">Do Not Sell or Share</a>
            <a href="/legal/dmca"              target="_blank" rel="noreferrer" className="underline hover:text-foreground text-foreground/70">DMCA</a>
            {role === "buyer" && (
              <a href="/legal/insurance"       target="_blank" rel="noreferrer" className="underline hover:text-foreground text-foreground/70">Trade Credit Insurance</a>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────
function Stepper({ step, role }) {
  const labels = role === "buyer"
    ? ["You", "Business", "Identity", "Categories", "Boost score"]
    : ["You", "Business", "Identity", "Categories"];
  return (
    <div className="mt-7 flex items-center gap-1.5 text-[10px] mono uppercase tracking-[0.18em] text-foreground/50"
      data-testid={`reg-stepper-${step}`}>
      {labels.map((l, i) => {
        const n = i + 1;
        const active = n === step;
        const done = n < step;
        const isOptional = n === 5;
        return (
          <span key={l} className="flex items-center gap-1.5">
            <span className={`w-5 h-5 rounded-full grid place-items-center text-[10px] ${
              done ? "bg-foreground text-background"
              : active ? "bg-accent text-foreground"
              : "bg-secondary text-foreground/45"
            }`}>
              {done ? <CheckCircle2 className="w-3 h-3"/> : n}
            </span>
            <span className={active ? "text-foreground font-semibold" : "text-foreground/40"}>
              {l}{isOptional && <span className="text-foreground/30 normal-case"> (optional)</span>}
            </span>
            {i < labels.length - 1 && <span className="text-foreground/20 mx-1">—</span>}
          </span>
        );
      })}
    </div>
  );
}


// Step 5 (seller) — optional Allianz Trade master-policy intake.
// Account is already created. User can opt in (5+ buyer roster) or skip
// and apply later from /seller/insurance.
function Step5SellerInsurance({ onDone }) {
  const [phase, setPhase] = useState("offer"); // offer | form | submitted | error
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const handleSkip = async () => {
    setSubmitting(true);
    try {
      const api = (await import("../lib/api")).default;
      await api.post("/seller/master-policy/decline-coverage");
      toast.success("Saved. You can apply for coverage anytime from the Insurance tab.");
      onDone();
    } catch {
      // Even on error, just route through — preference is non-critical.
      onDone();
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (payload) => {
    setSubmitting(true);
    setErr("");
    try {
      const api = (await import("../lib/api")).default;
      await api.post("/seller/master-policy/apply", payload);
      setPhase("submitted");
      toast.success("Master-policy application submitted to Allianz Trade.");
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (phase === "submitted") {
    return (
      <div className="mt-7 space-y-5" data-testid="reg-step-5-seller-submitted">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#0d8f4a]">
          <CheckCircle2 className="w-3.5 h-3.5" />
          Application submitted
        </div>
        <h2 className="display text-3xl sm:text-4xl font-semibold leading-tight">
          Allianz Trade is reviewing your master policy.
        </h2>
        <p className="text-foreground/70 leading-relaxed text-[15px]">
          Underwriting typically takes <strong>3–10 business days</strong>. We'll
          email you the moment your policy is issued. In the meantime, you can
          start onboarding buyers — per-buyer covers will auto-issue as soon as
          your master policy goes live.
        </p>
        <Button
          onClick={onDone}
          data-testid="reg-seller-ins-continue-btn"
          className="w-full rounded-full h-11 bg-foreground text-background hover:bg-foreground/90 text-[14px] font-medium"
        >
          Continue to dashboard <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    );
  }

  if (phase === "form") {
    return (
      <div className="mt-7 space-y-5" data-testid="reg-step-5-seller-form">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-foreground/50">
          <ShieldCheck className="w-3.5 h-3.5" />
          Allianz Trade master policy · application
        </div>
        <h2 className="display text-2xl sm:text-3xl font-semibold leading-tight">
          Tell Allianz about your buyers.
        </h2>
        <p className="text-foreground/65 leading-relaxed text-[14px]">
          List your 5+ largest buyers below. Allianz uses this to size your
          master policy. You can edit it anytime from the Insurance tab.
        </p>
        <MasterPolicyApplicationForm
          onSubmit={handleSubmit}
          onCancel={() => setPhase("offer")}
          submitting={submitting}
          error={err}
          submitLabel="Submit to Allianz Trade"
          compact
        />
      </div>
    );
  }

  // Default — offer screen ("do you want coverage?")
  return (
    <div className="mt-7 space-y-5" data-testid="reg-step-5-seller-offer">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-foreground/50">
        <ShieldCheck className="w-3.5 h-3.5" />
        Trade credit insurance · optional
      </div>
      <h2 className="display text-3xl sm:text-4xl font-semibold leading-tight">
        Want Allianz Trade to insure your receivables?
      </h2>
      <p className="text-foreground/70 leading-relaxed text-[15px]">
        Apply for a master policy now — Allianz covers up to 90% of any unpaid
        invoice. Most policies are approved in 3–10 business days. Once active,
        every new buyer that signs up to your store gets auto-evaluated for
        cover. Skip for now and apply later from the Insurance tab — your
        choice.
      </p>
      <div className="rounded-xl border border-foreground/15 bg-emerald-50/40 p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-emerald-500/15 grid place-items-center shrink-0">
            <ShieldCheck className="w-4.5 h-4.5 text-emerald-700" />
          </div>
          <div className="text-[13px] leading-snug text-foreground/85">
            <strong>What you'll need:</strong> at least 5 buyers with EIN, legal
            address, and annual sales volume (last 12 mo + expected next 12 mo).
            Takes about 5 minutes. No upfront cost. Premium is pass-through and
            only billed against approved cover.
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={handleSkip}
          disabled={submitting}
          data-testid="reg-seller-ins-skip"
          className="rounded-full h-11"
        >
          Skip for now
        </Button>
        <Button
          type="button"
          onClick={() => setPhase("form")}
          data-testid="reg-seller-ins-apply"
          className="rounded-full h-11 bg-foreground text-background hover:bg-foreground/90"
        >
          Apply for coverage <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </div>
    </div>
  );
}


// Step 5 — optional Plaid bank-link to boost the underwriting score.
// Buyers only. Always skippable. Account is already created at this point.
function Step5BoostScore({ boostScore, onConnected, onDone }) {
  if (boostScore) {
    return (
      <div className="mt-7 space-y-5" data-testid="reg-step-5-success">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#0d8f4a]">
          <CheckCircle2 className="w-3.5 h-3.5" />
          Bank connected · Score updated
        </div>
        <h2 className="display text-3xl sm:text-4xl font-semibold leading-tight">
          {boostScore.institution} linked. You're underwriting-ready.
        </h2>
        <p className="text-foreground/70 leading-relaxed text-[15px]">
          We read 24 months of cashflow. Your verqo Trade Score will reflect the new
          signals at first checkout — typically <strong>+90 to +200 points</strong>
          {" "}for healthy accounts.
        </p>
        <div className="rounded-xl border-2 border-foreground bg-accent/10 p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-foreground/5 grid place-items-center">
              <Landmark className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">{boostScore.institution}</div>
              <div className="text-xs text-foreground/55">
                {boostScore.accounts} ACH-ready account{boostScore.accounts === 1 ? "" : "s"} · linked just now
              </div>
            </div>
          </div>
        </div>
        <Button
          onClick={onDone}
          data-testid="reg-boost-continue-btn"
          className="w-full rounded-full h-11 bg-foreground text-background hover:bg-foreground/90 text-[14px] font-medium">
          Continue to dashboard <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-7 space-y-5" data-testid="reg-step-5-offer">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-foreground/50">
        <Sparkles className="w-3.5 h-3.5" />
        Boost your score · optional
      </div>
      <h2 className="display text-3xl sm:text-4xl font-semibold leading-tight">
        Add 90+ points to your verqo Trade Score in 30 seconds.
      </h2>
      <p className="text-foreground/70 leading-relaxed text-[15px]">
        Connect your business bank read-only. We pull 24 months of cashflow to
        underwrite a real Net-30/60/90 limit at your first checkout. No statements,
        no PDFs, no waiting.
      </p>

      <div className="rounded-xl border border-border p-5 bg-background">
        <div className="flex items-center justify-between text-[10px] mono uppercase tracking-[0.18em] text-foreground/50 mb-3">
          <span>Your starting score</span>
          <span>With Plaid connected</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex-1 rounded-lg border border-border p-3.5">
            <div className="text-2xl display font-semibold text-foreground/40">—</div>
            <div className="text-[11px] text-foreground/50 mt-0.5">Thin file</div>
          </div>
          <ArrowRight className="w-4 h-4 text-foreground/40" />
          <div className="flex-1 rounded-lg border-2 border-foreground p-3.5 bg-accent/10">
            <div className="text-2xl display font-semibold">724</div>
            <div className="text-[11px] text-foreground/70 mt-0.5">
              <TrendingUp className="w-3 h-3 inline mr-0.5" />
              Avg connected
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 text-[11.5px] text-foreground/55 bg-foreground/[0.04] rounded-md px-3 py-2.5">
        <Lock className="w-3.5 h-3.5 shrink-0" />
        Read-only · powered by Plaid · we can't move money or see passwords · revoke anytime in Settings.
      </div>

      <div className="flex flex-col-reverse sm:flex-row gap-3 pt-1">
        <Button
          variant="ghost"
          onClick={onDone}
          data-testid="reg-boost-skip-btn"
          className="rounded-full h-11 px-5 text-foreground/70 hover:text-foreground">
          Skip — I'll do this later
        </Button>
        <PlaidLinkButton
          onConnected={onConnected}
          label="Connect bank · Boost my score"
        />
      </div>

      <div className="flex items-center justify-center gap-1.5 text-[11px] text-foreground/45">
        <Clock className="w-3 h-3" />
        Average flow: 28 seconds
      </div>
    </div>
  );
}

function Field({ label, hint, icon: Icon, children }) {
  return (
    <div>
      <Label className="text-[12.5px] flex items-center gap-1.5">
        {Icon && <Icon className="w-3.5 h-3.5 text-foreground/50"/>}
        {label}
      </Label>
      <div className="mt-1.5">{children}</div>
      {hint && <div className="text-[11px] text-foreground/50 mt-1">{hint}</div>}
    </div>
  );
}
